import { ShippingMethodsQuery } from '@/__generated__/graphql';
import { useMutation, useQuery } from '@apollo/client';
import {
  ActionIcon,
  Button,
  Group,
  MultiSelect,
  NumberInput,
  Stack,
  TextInput,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { modals } from '@mantine/modals';
import { IconEdit } from '@tabler/icons-react';
import { DataTable } from 'mantine-datatable';
import { paymentMethods, shippingMethods, updateShippingMethod } from '../graphql/query';

const UpsertShippingMethodModal = ({
  shippingMethod,
}: {
  shippingMethod: ShippingMethodsQuery['shippingMethods'][number];
}) => {
  const { data, loading } = useQuery(paymentMethods);

  const [upsertShippingMethod, { loading: upserting }] = useMutation(updateShippingMethod, {
    refetchQueries: [shippingMethods],
    onCompleted: () => modals.closeAll(),
  });

  const form = useForm({
    initialValues: {
      name: shippingMethod.name,
      description: shippingMethod.description,
      country: shippingMethod.country,
      fee: shippingMethod.fee,
      paymentMethods: shippingMethod.paymentMethods?.map((p) => p.id),
    },
  });

  return (
    <form
      onSubmit={form.onSubmit(async (values) => {
        const currentPaymentMethodIds = shippingMethod.paymentMethods?.map((p) => p.id); // Existing IDs
        const newPaymentMethodIds = values.paymentMethods; // Selected IDs

        const disconnectIds = currentPaymentMethodIds?.filter(
          (id) => !newPaymentMethodIds?.includes(id)
        );
        const connectIds = newPaymentMethodIds?.filter(
          (id) => !currentPaymentMethodIds?.includes(id)
        );

        await upsertShippingMethod({
          variables: {
            where: { id: shippingMethod.id },
            data: {
              name: { set: values.name },
              description: { set: values.description },
              country: { set: values.country },
              fee: { set: values.fee },
              paymentMethods: {
                disconnect: disconnectIds?.map((id) => ({ id })),
                connect: connectIds?.map((id) => ({ id })),
              },
            },
          },
        });
      })}
    >
      <Stack>
        <TextInput label="Name" {...form.getInputProps('name')} />
        <TextInput label="Description" {...form.getInputProps('description')} />
        <TextInput label="Country" {...form.getInputProps('country')} />
        <NumberInput label="Fee" {...form.getInputProps('fee')} />
        <MultiSelect
          label="Payment methods"
          data={data?.paymentMethods.map((p) => ({ value: p.id, label: p.name }))}
          disabled={loading}
          {...form.getInputProps('paymentMethods')}
        />
        <Button type="submit" loading={upserting}>
          Save
        </Button>
      </Stack>
    </form>
  );
};

export function Settings() {
  const { data, loading } = useQuery(shippingMethods);

  return (
    <Stack>
      <DataTable
        withTableBorder
        striped
        highlightOnHover
        borderRadius="sm"
        minHeight={200}
        records={data?.shippingMethods ?? []}
        fetching={loading}
        columns={[
          { accessor: 'id' },
          { accessor: 'name' },
          { accessor: 'country' },
          { accessor: 'description' },
          { accessor: 'fee' },
          {
            accessor: 'actions',
            render: (shippingMethod) => (
              <Group>
                <ActionIcon
                  size="sm"
                  variant="subtle"
                  onClick={(e) => {
                    e.stopPropagation();
                    modals.open({
                      title: `Edit ${shippingMethod.id}`,
                      children: <UpsertShippingMethodModal shippingMethod={shippingMethod} />,
                      size: 'xl',
                    });
                  }}
                >
                  <IconEdit size={16} />
                </ActionIcon>
              </Group>
            ),
          },
        ]}
      />
    </Stack>
  );
}
